import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import certificateImg from '../Assets/image.png';

const ScrambleText = ({ text, className }) => {
  const [displayText, setDisplayText] = useState(text);
  const intervalRef = useRef(null);
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()_+{}|:<>?";

  const startAnimation = () => {
    let iteration = 0;
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setDisplayText((prev) =>
        text.split("").map((letter, index) => {
          if (index < iteration) return text[index];
          if (text[index] === " ") return " ";
          return chars[Math.floor(Math.random() * chars.length)];
        }).join("")
      );
      if (iteration >= text.length) clearInterval(intervalRef.current);
      iteration += 1 / 3;
    }, 30);
  };

  useEffect(() => {
    // Triggers animation on initial page load/refresh
    startAnimation();
    return () => clearInterval(intervalRef.current);
  }, []);

  return <span className={className} onMouseEnter={startAnimation}>{displayText}</span>;
};

const CountUpAnimation = ({ end, suffix = "", className, style }) => {
  const [count, setCount] = useState(0);
  const countRef = useRef(null);

  useEffect(() => {
    let observer;
    let startTimestamp = null;
    const duration = 2000;

    const step = (timestamp) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(easeOutQuart * end));
      if (progress < 1) {
        window.requestAnimationFrame(step);
      }
    };

    observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          window.requestAnimationFrame(step);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (countRef.current) {
      observer.observe(countRef.current);
    }

    return () => {
      if (observer) observer.disconnect();
    };
  }, [end]);

  return <span ref={countRef} className={className} style={style}>{count.toLocaleString()}{suffix}</span>;
};

const Home = () => {
  const [activeFaq, setActiveFaq] = useState(null);
  const toggleFaq = (index) => setActiveFaq(activeFaq === index ? null : index);
  const navigate = useNavigate();

  const features = [
    {
      iconColor: 'bg-orange-500',
      blobColor: 'bg-orange-50/70',
      icon: <svg className="w-5 h-5 md:w-6 md:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M13 10V3L4 14h7v7l9-11h-7z"></path></svg>,
      title: 'Structured Assignment-Based Internships',
      desc: 'Our internships are built around predefined industry-style projects. You receive a set of curated assignments and complete selected ones to demonstrate real skills instead of passive learning.'
    },
    {
      iconColor: 'bg-blue-500',
      blobColor: 'bg-blue-50/70',
      icon: <svg className="w-5 h-5 md:w-6 md:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>,
      title: 'Flexible Learning Schedule',
      desc: 'Work on assignments at your own pace while managing college, work, or personal commitments. No rigid class timings.'
    },
    {
      iconColor: 'bg-emerald-500',
      blobColor: 'bg-emerald-50/70',
      icon: <svg className="w-5 h-5 md:w-6 md:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path></svg>,
      title: 'Verified Certificates',
      desc: 'Earn a certificate with a unique verification ID after completing required assignments successfully.'
    },
    {
      iconColor: 'bg-fuchsia-500',
      blobColor: 'bg-fuchsia-50/70',
      icon: <svg className="w-5 h-5 md:w-6 md:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path></svg>,
      title: 'Real Portfolio Projects',
      desc: 'Choose from multiple practical projects designed to reflect real-world development tasks and strengthen your portfolio.'
    }
  ];

  const techCategories = [
    {
      title: "Frontend Development",
      desc: "Building interactive user interfaces",
      iconColor: "text-blue-500",
      iconBg: "bg-blue-100/60",
      icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><rect x="2" y="3" width="20" height="14" rx="2" ry="2" strokeWidth="2"></rect><line x1="8" y1="21" x2="16" y2="21" strokeWidth="2"></line><line x1="12" y1="17" x2="12" y2="21" strokeWidth="2"></line></svg>,
      skills: [
        { name: "HTML", dot: "bg-orange-600" },
        { name: "CSS", dot: "bg-blue-500" },
        { name: "REACT", dot: "bg-cyan-400" },
        { name: "ANGULAR", dot: "bg-red-600" },
        { name: "VUE", dot: "bg-green-500" },
        { name: "JAVASCRIPT", dot: "bg-yellow-400" },
        { name: "TYPESCRIPT", dot: "bg-blue-600" }
      ]
    },
    {
      title: "Backend Systems",
      desc: "Powering robust server-side logic",
      iconColor: "text-green-600",
      iconBg: "bg-green-100/60",
      icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><ellipse cx="12" cy="5" rx="9" ry="3" strokeWidth="2"></ellipse><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" strokeWidth="2"></path><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" strokeWidth="2"></path></svg>,
      skills: [
        { name: "PYTHON", dot: "bg-yellow-500" },
        { name: "JAVA", dot: "bg-red-500" },
        { name: "C++", dot: "bg-blue-700" },
        { name: "NODE.JS", dot: "bg-green-600" }
      ]
    },
    {
      title: "DevOps & Cloud",
      desc: "Deploying scalable infrastructure",
      iconColor: "text-purple-500",
      iconBg: "bg-purple-100/60",
      icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z"></path></svg>,
      skills: [
        { name: "AWS", dot: "bg-orange-500" },
        { name: "KUBERNETES", dot: "bg-blue-700" },
        { name: "DOCKER", dot: "bg-blue-400" },
        { name: "GIT", dot: "bg-red-500" },
        { name: "LINUX", dot: "bg-yellow-300" }
      ]
    },
    {
      title: "AI & Data Science",
      desc: "Innovating with machine learning",
      iconColor: "text-orange-500",
      iconBg: "bg-orange-100/60",
      icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"></path></svg>,
      skills: [
        { name: "TENSORFLOW", dot: "bg-orange-500" },
        { name: "PYTORCH", dot: "bg-red-400" },
        { name: "JUPYTER", dot: "bg-orange-400" }
      ]
    }
  ];

  const internshipsData = [
    {
      icon: <svg className="w-5 h-5 md:w-6 md:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"></path></svg>,
      title: 'Web Development',
      desc: 'Build modern, responsive websites using HTML, CSS, JavaScript, and React. Work on real-world projects.',
      bgCode: 'bg-[#0EA5E9]',
      blobColor: 'bg-sky-50'
    },
    {
      icon: <svg className="w-5 h-5 md:w-6 md:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"></path></svg>,
      title: 'App Development',
      desc: 'Create mobile applications for Android using Java/Kotlin or cross-platform apps with Flutter.',
      bgCode: 'bg-[#22C55E]',
      blobColor: 'bg-green-50'
    },
    {
      icon: <svg className="w-5 h-5 md:w-6 md:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"></path></svg>,
      title: 'Machine Learning',
      desc: 'Dive into AI/ML. Work with Python, TensorFlow, and Scikit-learn to build predictive models.',
      bgCode: 'bg-[#8B5CF6]',
      blobColor: 'bg-purple-50'
    },
    {
      icon: <svg className="w-5 h-5 md:w-6 md:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"></path></svg>,
      title: 'C++ Programming',
      desc: 'Master the fundamentals of C++ and Object-Oriented Programming through practical tasks.',
      bgCode: 'bg-[#EF4444]',
      blobColor: 'bg-red-50'
    },
    {
      icon: <svg className="w-5 h-5 md:w-6 md:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"></path></svg>,
      title: 'Java Programming',
      desc: 'Learn Java core concepts, collections, and build robust console-based applications.',
      bgCode: 'bg-[#F97316]',
      blobColor: 'bg-orange-50'
    },
    {
      icon: <svg className="w-5 h-5 md:w-6 md:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"></path></svg>,
      title: 'Graphic Design',
      desc: 'Unleash your creativity. Design social media posts, logos, and UI/UX mockups.',
      bgCode: 'bg-[#EC4899]',
      blobColor: 'bg-pink-50'
    }
  ];

  // How It Works data - removed 'active' default so they all act identical until hovered
  const howItWorks = [
    { 
      step: '01', 
      title: 'Apply', 
      desc: "Fill out the simple registration form. It's completely free and takes less than 2 minutes.", 
      icon: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#0284C7]"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="8.5" cy="7" r="4"></circle><line x1="20" y1="8" x2="20" y2="14"></line><line x1="23" y1="11" x2="17" y2="11"></line></svg>
    },
    { 
      step: '02', 
      title: 'Selection', 
      desc: "Get your selection letter via email. Join our community channels for updates. You'll receive a curated set of practical assignments for your selected domain.", 
      icon: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#0284C7]"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>
    },
    { 
      step: '03', 
      title: 'Complete Tasks', 
      desc: "Receive task lists for your domain. Complete them within the 4-week deadline. Only completed assignments are evaluated for certification.", 
      icon: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#0284C7]"><path d="M9 11l3 3L22 4"></path><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path></svg>
    },
    { 
      step: '04', 
      title: 'Get Certified', 
      desc: "Submit your work. Upon verification, receive your internship completion certificate.", 
      icon: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#0284C7]"><circle cx="12" cy="8" r="7"></circle><polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"></polyline></svg>
    }
  ];

  const tools = [
    { 
      icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline></svg>, 
      title: 'Check ATS Score', 
      desc: 'Is your resume beating the bots? Upload your CV to get an instant score out of 100 and actionable improvements. Understand how recruiters and ATS systems evaluate your profile.', 
      color: 'blue', 
      bgCode: 'bg-[#0EA5E9]', 
      blobColor: 'bg-sky-50',
      linkText: 'Check Now',
      route: '/ats-score'
    },
    { 
      icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>, 
      title: 'AI Resume Scanner', 
      desc: 'Compare your resume against job descriptions to see how well you match and what keywords are missing. Improve your resume before applying to internships or jobs.', 
      color: 'pink', 
      bgCode: 'bg-[#F43F5E]', 
      blobColor: 'bg-rose-50',
      linkText: 'Scan Resume',
      route: '/resume-scanner'
    },
    { 
      icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>, 
      title: 'Job Email Builder', 
      desc: 'Generate professional cold emails in seconds and get a curated list of HR contacts from top tech companies. Create outreach emails that increase your chances of getting responses.', 
      color: 'purple', 
      bgCode: 'bg-[#8B5CF6]', 
      blobColor: 'bg-purple-50',
      linkText: 'Generate Email',
      route: '/email-builder'
    },
    { 
      icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"></rect><rect x="14" y="3" width="7" height="7"></rect><rect x="14" y="14" width="7" height="7"></rect><rect x="3" y="14" width="7" height="7"></rect></svg>, 
      title: 'Student Dashboard', 
      desc: 'View your performance, check your certificate status, and manage your learning progress in one place.', 
      color: 'red', 
      bgCode: 'bg-[#EF4444]', 
      blobColor: 'bg-red-50',
      linkText: 'Go to Dashboard',
      route: '/dashboards'
    }
  ];

  const reviews = [
    { initial: 'P', name: 'Priya Singh', role: 'App Development Intern', quote: 'I built my first Android app during this internship. The mentorship and community support were amazing.' },
    { initial: 'A', name: 'Amit Patel', role: 'Data Science Intern', quote: 'Great exposure to real-world datasets. The tasks helped me build a strong portfolio.' },
    { initial: 'S', name: 'Sneha Gupta', role: 'UI/UX Design Intern', quote: 'The design assignments were challenging and practical. Earning the verified certificate validated my skills in UI/UX.' },
    { initial: 'V', name: 'Vikram Malhotra', role: 'Cyber Security Intern', quote: 'The network security tasks simulated real scenarios. Completing these assignments proved my skills far better than just watching tutorials.' },
    { initial: 'R', name: 'Rahul Sharma', role: 'Web Development Intern', quote: 'Completing the assignments helped me understand real development workflows. The projects I submitted are now part of my portfolio.' }
  ];

  const faqs = [
    { question: 'Is the internship really free?', answer: 'Yes! Our virtual internships are 100% free. We believe in democratizing education and providing equal opportunities for all students.' },
    { question: 'How do I receive the certificate?', answer: 'Upon successful completion of the assigned tasks within the 4-week duration, you will receive a verifiable completion certificate via email.' },
    { question: 'Can I apply for multiple domains?', answer: 'Yes, you can apply for multiple domains if you have the required skills and time to manage the tasks.' },
    { question: 'Is there any selection process?', answer: 'We have a screening process based on your application form details. Make sure to fill it out carefully.' },
    { question: 'Is it work from home?', answer: 'Yes, all our internships are Virtual (Remote). You can work from anywhere.' }
  ];

  const companyData = [
    { name: 'Microsoft', color: 'text-green-600', hoverGlow: 'hover:shadow-green-500/30 hover:border-green-200' },
    { name: 'Amazon', color: 'text-yellow-600', hoverGlow: 'hover:shadow-yellow-500/30 hover:border-yellow-200' },
    { name: 'Meta', color: 'text-blue-600', hoverGlow: 'hover:shadow-blue-500/30 hover:border-blue-200' },
    { name: 'Netflix', color: 'text-red-600', hoverGlow: 'hover:shadow-red-500/30 hover:border-red-200' },
    { name: 'Apple', color: 'text-slate-900', hoverGlow: 'hover:shadow-slate-500/30 hover:border-slate-300' },
    { name: 'Google', color: 'text-blue-500', hoverGlow: 'hover:shadow-blue-500/30 hover:border-blue-200' },
    { name: 'IBM', color: 'text-indigo-600', hoverGlow: 'hover:shadow-indigo-500/30 hover:border-indigo-200' },
    { name: 'Oracle', color: 'text-red-500', hoverGlow: 'hover:shadow-red-500/30 hover:border-red-200' },
    { name: 'Adobe', color: 'text-red-600', hoverGlow: 'hover:shadow-red-600/30 hover:border-red-200' },
    { name: 'Intel', color: 'text-blue-500', hoverGlow: 'hover:shadow-blue-500/30 hover:border-blue-200' },
    { name: 'Samsung', color: 'text-blue-700', hoverGlow: 'hover:shadow-blue-700/30 hover:border-blue-300' },
    { name: 'Uber', color: 'text-slate-800', hoverGlow: 'hover:shadow-slate-600/30 hover:border-slate-300' },
    { name: 'LinkedIn', color: 'text-sky-600', hoverGlow: 'hover:shadow-sky-500/30 hover:border-sky-200' },
    { name: 'Tesla', color: 'text-red-600', hoverGlow: 'hover:shadow-red-600/30 hover:border-red-200' }
  ];

  const companyDataBottom = [
    { name: 'Cisco', color: 'text-sky-600', hoverGlow: 'hover:shadow-sky-500/30 hover:border-sky-200' },
    { name: 'Spotify', color: 'text-green-500', hoverGlow: 'hover:shadow-green-500/30 hover:border-green-200' },
    { name: 'Twitter', color: 'text-sky-400', hoverGlow: 'hover:shadow-sky-400/30 hover:border-sky-200' },
    { name: 'Airbnb', color: 'text-rose-500', hoverGlow: 'hover:shadow-rose-500/30 hover:border-rose-200' },
    { name: 'Stripe', color: 'text-indigo-500', hoverGlow: 'hover:shadow-indigo-500/30 hover:border-indigo-200' },
    { name: 'PayPal', color: 'text-blue-800', hoverGlow: 'hover:shadow-blue-800/30 hover:border-blue-300' },
    { name: 'Salesforce', color: 'text-sky-500', hoverGlow: 'hover:shadow-sky-500/30 hover:border-sky-200' },
    { name: 'Nvidia', color: 'text-green-600', hoverGlow: 'hover:shadow-green-600/30 hover:border-green-200' },
    { name: 'AMD', color: 'text-slate-800', hoverGlow: 'hover:shadow-slate-800/30 hover:border-slate-300' },
    { name: 'SAP', color: 'text-blue-500', hoverGlow: 'hover:shadow-blue-500/30 hover:border-blue-200' },
    { name: 'Dropbox', color: 'text-blue-600', hoverGlow: 'hover:shadow-blue-600/30 hover:border-blue-200' },
    { name: 'Snowflake', color: 'text-sky-400', hoverGlow: 'hover:shadow-sky-400/30 hover:border-sky-200' },
    { name: 'Atlassian', color: 'text-blue-600', hoverGlow: 'hover:shadow-blue-600/30 hover:border-blue-200' },
    { name: 'Pinterest', color: 'text-red-600', hoverGlow: 'hover:shadow-red-600/30 hover:border-red-200' }
  ];

  const gradientTextClass = "inline-block bg-gradient-to-r from-primary via-purple-500 to-primary text-transparent bg-clip-text bg-[length:200%_auto] animate-shimmer min-w-[200px] tabular-nums cursor-default";

  return (
    <>
      <style>
        {`@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700;800;900&display=swap');`}
      </style>
      <main className="flex-grow overflow-x-hidden bg-[#F8FAFC]">
        
        {/* HERO SECTION */}
        <section className="pt-[140px] md:pt-[180px] pb-[60px] md:pb-[100px] px-5 text-center">
          <div className="inline-flex items-center justify-center py-2 px-5 bg-[#F0F8FF] text-[#0284C7] rounded-full text-xs font-bold mb-[35px] tracking-wide border border-[#E0F2FE]">
            <svg className="w-3.5 h-3.5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
            </svg>
            #1 PLATFORM FOR VIRTUAL INTERNSHIPS
          </div>

          <h1 className="text-[clamp(2.8rem,7vw,6.5rem)] leading-[1.1] mb-6 font-medium" style={{ fontFamily: "'Playfair Display', serif" }}>
            <span className="text-[#0F172A] tracking-tight">Where Learning Meets</span><br />
            <ScrambleText text="Real World Experience" className="inline-block bg-gradient-to-r from-blue-600 to-purple-500 text-transparent bg-clip-text tracking-tight" />
          </h1>

          <p className="text-[#475569] text-base md:text-[19px] mb-[45px] max-w-3xl mx-auto font-medium leading-relaxed">
            Kickstart your career with our free virtual internship program. Gain hands-on<br className="hidden md:block"/> experience, work on live projects, and get certified.
          </p>

          <div className="flex flex-col sm:flex-row justify-center gap-[15px] md:gap-[20px]">
            <button onClick={() => navigate('/apply')} className="py-3.5 px-8 rounded-xl font-bold text-sm md:text-base bg-[#0F172A] text-white transition hover:opacity-90 inline-flex justify-center items-center gap-2 shadow-lg shadow-slate-900/20">
              Apply for Internship
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path></svg>
            </button>
            <button onClick={() => navigate('/verify')} className="py-3.5 px-8 rounded-xl font-bold text-sm md:text-base bg-white border border-slate-200 text-[#0F172A] transition hover:bg-slate-50 inline-flex justify-center items-center gap-2 shadow-sm">
              Verify Certificate
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
            </button>
          </div>
        </section>

        {/* TRUSTED BY STUDENTS (STATS BLOCK) */}
        <section className="py-[60px] md:py-[90px] bg-white">
          <div className="max-w-[1200px] mx-auto px-5 text-center">
            
            <div className="inline-flex items-center justify-center py-2.5 px-6 bg-[#F0F8FF] text-[#0284C7] rounded-full text-[9px] md:text-[11px] font-black mb-8 tracking-[1.5px] uppercase border border-[#E0F2FE]">
              Real-time platform growth powered by active learners completing assignments.
            </div>
            
            <h2 className="text-[40px] md:text-[56px] font-bold leading-tight mb-5 tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              <span className="text-[#0F172A]">Trusted by </span><span className="text-[#0284C7]">Students Worldwide</span>
            </h2>
            
            <p className="text-[#475569] font-medium text-base md:text-lg max-w-2xl mx-auto mb-16">
              SENVRA AI has helped learners build skills, complete real projects, and grow their careers across multiple countries.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch mb-12">
              
              {/* Card 1 */}
              <div className="bg-white rounded-[32px] p-8 md:p-10 border border-slate-100 shadow-[0_8px_30px_rgba(0,0,0,0.06)] relative overflow-hidden text-center flex flex-col h-full group transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_12px_40px_rgba(0,0,0,0.12)]">
                <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-50/70 rounded-bl-[100px] -z-10 transition-transform duration-500 group-hover:scale-110"></div>
                <div className="flex items-center justify-center gap-2 mb-6">
                  <div className="w-2 h-2 rounded-full bg-emerald-400"></div>
                  <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">Live Platform Metrics</span>
                </div>
                <div className="flex-grow flex flex-col justify-center">
                  <CountUpAnimation end={1112262} className="text-[42px] md:text-[48px] font-medium mb-4 inline-block bg-gradient-to-r from-[#0284C7] via-[#8B5CF6] to-[#0284C7] text-transparent bg-clip-text" style={{ fontFamily: "'Playfair Display', serif" }} />
                  <h4 className="text-[15px] font-black text-[#0F172A] mb-3 uppercase tracking-wide leading-snug">Students<br/>Joined</h4>
                  <p className="text-[13px] text-slate-500 font-medium leading-relaxed px-2">From learners across diverse tech backgrounds</p>
                </div>
              </div>

              {/* Card 2 */}
              <div className="bg-white rounded-[32px] p-8 md:p-10 border border-slate-100 shadow-[0_8px_30px_rgba(0,0,0,0.06)] relative overflow-hidden text-center flex flex-col h-full group transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_12px_40px_rgba(0,0,0,0.12)]">
                <div className="absolute top-0 right-0 w-32 h-32 bg-fuchsia-50/70 rounded-bl-[100px] -z-10 transition-transform duration-500 group-hover:scale-110"></div>
                <div className="flex items-center justify-center gap-2 mb-6">
                  <div className="w-2 h-2 rounded-full bg-emerald-400"></div>
                  <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">Live Platform Metrics</span>
                </div>
                <div className="flex-grow flex flex-col justify-center">
                  <CountUpAnimation end={774666} className="text-[42px] md:text-[48px] font-medium mb-4 inline-block bg-gradient-to-r from-[#0284C7] via-[#8B5CF6] to-[#0284C7] text-transparent bg-clip-text" style={{ fontFamily: "'Playfair Display', serif" }} />
                  <h4 className="text-[15px] font-black text-[#0F172A] mb-3 uppercase tracking-wide leading-snug">Certificates<br/>Issued</h4>
                  <p className="text-[13px] text-slate-500 font-medium leading-relaxed px-2">Issued after verified assignment completion</p>
                </div>
              </div>

              {/* Card 3 */}
              <div className="bg-white rounded-[32px] p-8 md:p-10 border border-slate-100 shadow-[0_8px_30px_rgba(0,0,0,0.06)] relative overflow-hidden text-center flex flex-col h-full group transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_12px_40px_rgba(0,0,0,0.12)]">
                <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-50/70 rounded-bl-[100px] -z-10 transition-transform duration-500 group-hover:scale-110"></div>
                <div className="flex items-center justify-center gap-2 mb-6">
                  <div className="w-2 h-2 rounded-full bg-emerald-400"></div>
                  <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">Live Platform Metrics</span>
                </div>
                <div className="flex-grow flex flex-col justify-center">
                  <CountUpAnimation end={694} className="text-[42px] md:text-[48px] font-medium mb-4 inline-block bg-gradient-to-r from-[#0284C7] via-[#8B5CF6] to-[#0284C7] text-transparent bg-clip-text" style={{ fontFamily: "'Playfair Display', serif" }} />
                  <h4 className="text-[15px] font-black text-[#0F172A] mb-3 uppercase tracking-wide leading-snug">Services<br/>&nbsp;</h4>
                  <p className="text-[13px] text-slate-500 font-medium leading-relaxed px-2">Tools, platforms, and active services combined</p>
                </div>
              </div>

              {/* Card 4 */}
              <div className="bg-white rounded-[32px] p-8 md:p-10 border border-slate-100 shadow-[0_8px_30px_rgba(0,0,0,0.06)] relative overflow-hidden text-center flex flex-col h-full group transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_12px_40px_rgba(0,0,0,0.12)]">
                <div className="absolute top-0 right-0 w-32 h-32 bg-orange-50/70 rounded-bl-[100px] -z-10 transition-transform duration-500 group-hover:scale-110"></div>
                <div className="flex items-center justify-center gap-2 mb-6">
                  <div className="w-2 h-2 rounded-full bg-emerald-400"></div>
                  <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">Live Platform Metrics</span>
                </div>
                <div className="flex-grow flex flex-col justify-center">
                  <CountUpAnimation end={25} suffix="+" className="text-[42px] md:text-[48px] font-medium mb-4 inline-block bg-gradient-to-r from-[#0284C7] via-[#8B5CF6] to-[#0284C7] text-transparent bg-clip-text" style={{ fontFamily: "'Playfair Display', serif" }} />
                  <h4 className="text-[15px] font-black text-[#0F172A] mb-3 uppercase tracking-wide leading-snug">Countries<br/>&nbsp;</h4>
                  <p className="text-[13px] text-slate-500 font-medium leading-relaxed px-2">Global participation from learners worldwide</p>
                </div>
              </div>

            </div>

            {/* QUOTE & BUTTON */}
            <div className="mt-12 flex flex-col items-center">
              <p className="text-[#64748B] italic text-[14px] md:text-[15px] mb-8 font-medium">
                "Our growth is driven by real learners completing real assignments — not passive course enrollments."
              </p>
              <button 
                onClick={() => navigate('/apply')} 
                className="bg-[#0284C7] hover:bg-[#0369A1] text-white font-bold py-3.5 px-8 rounded-lg text-[15px] transition-colors flex items-center gap-2 shadow-md shadow-sky-200"
              >
                Start Your Internship
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path></svg>
              </button>
            </div>
          </div>
        </section>

        {/* WHY CHOOSE SECTION */}
        <section className="py-[60px] md:py-[90px] text-center bg-[#F8FAFC]">
          <div className="max-w-[1200px] mx-auto px-5">
            <h2 className="text-[36px] md:text-[48px] font-bold tracking-tight mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              <span className="text-[#0F172A]">Why Choose </span><span className="text-[#0284C7]">SENVRA AI?</span>
            </h2>
            <p className="text-slate-500 text-base md:text-[17px] my-[20px] mx-auto max-w-[700px] font-medium leading-relaxed">
              Empowering the next generation of tech leaders through practical learning and industry-aligned internships.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-[25px] mt-[40px] md:mt-[50px] items-stretch">
              {features.map((feat, i) => (
                <div key={i} className="bg-white rounded-[24px] p-[30px] md:p-[35px] border border-slate-100 shadow-[0_8px_30px_rgba(0,0,0,0.04)] relative overflow-hidden text-left flex flex-col h-full group transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_12px_40px_rgba(0,0,0,0.12)]">
                  <div className={`absolute top-0 right-0 w-32 h-32 ${feat.blobColor} rounded-bl-[100px] -z-10 transition-transform duration-500 group-hover:scale-110`}></div>
                  <div className={`w-[45px] h-[45px] md:w-[50px] md:h-[50px] rounded-xl flex items-center justify-center mb-[20px] md:mb-[25px] ${feat.iconColor} shadow-md`}>
                    {feat.icon}
                  </div>
                  <h3 className="text-[18px] md:text-[20px] font-bold mb-[15px] leading-tight text-slate-900" style={{ fontFamily: "'Playfair Display', serif" }}>{feat.title}</h3>
                  <p className="text-slate-500 text-[14px] md:text-[15px] leading-relaxed flex-grow">{feat.desc}</p>
                </div>
              ))}
            </div>

            <div className="mt-12 md:mt-16 text-center">
              <p className="text-[#64748B] italic text-[14px] md:text-[15px] font-medium">
                "Our programs focus on practical execution — you don't just learn concepts, you prove your skills through completed assignments."
              </p>
            </div>
          </div>
        </section>

        {/* SKILLS & TOOLS SECTION */}
        <section className="py-[60px] md:py-[90px] bg-white text-center">
          <div className="max-w-[1200px] mx-auto px-5">
            <span className="block text-[10px] md:text-xs font-bold text-[#0284C7] tracking-[1.5px] uppercase mb-4">SKILLS & TOOLS</span>
            <h2 className="text-[32px] md:text-[46px] font-bold tracking-tight mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              <span className="text-[#0F172A]">Technologies You'll </span>
              <span className="text-[#0284C7]">Work With </span>
              <span className="text-[#0F172A]">During Your Internship</span>
            </h2>
            <p className="text-slate-500 text-base md:text-[17px] my-[10px] mx-auto max-w-[700px] font-medium leading-relaxed mb-[50px]">
              You'll apply these technologies while completing real assignments and portfolio projects.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-[25px] text-left items-stretch">
              {techCategories.map((category, idx) => (
                <div 
                  key={idx} 
                  className="group bg-[#F8FAFC] hover:bg-white rounded-[24px] p-[30px] md:p-[40px] border border-transparent hover:border-[#E2E8F0] hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)] flex flex-col h-full transition-all duration-300 ease-in-out cursor-pointer"
                >
                  <div className="flex items-center gap-4 mb-8">
                    <div className={`w-[45px] h-[45px] rounded-xl flex items-center justify-center ${category.iconBg} ${category.iconColor} shadow-sm shrink-0`}>
                      {category.icon}
                    </div>
                    <div>
                      <h3 className="text-[18px] md:text-[22px] font-bold text-slate-900 leading-tight mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>{category.title}</h3>
                      <p className="text-[13px] md:text-[14px] text-slate-500">{category.desc}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-3 mt-auto">
                    {category.skills.map((skill, sIdx) => (
                      <div key={sIdx} className="inline-flex items-center gap-2 py-2 px-4 bg-white border border-slate-200 rounded-full shadow-[0_2px_10px_rgba(0,0,0,0.02)] group-hover:border-slate-300 transition-all duration-300 hover:-translate-y-1 hover:shadow-md hover:bg-slate-50 cursor-pointer">
                        <span className={`w-2 h-2 rounded-full ${skill.dot}`}></span>
                        <span className="text-[11px] md:text-[12px] font-bold text-slate-700 tracking-wide">{skill.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* QUOTE & BUTTON for Skills Section */}
            <div className="mt-16 flex flex-col items-center">
              <p className="text-[#64748B] italic text-[14px] md:text-[15px] mb-8 font-medium">
                "Projects are designed to reflect real development workflows used in companies."
              </p>
              <button 
                onClick={() => navigate('/apply')} 
                className="bg-[#0F172A] hover:bg-[#1E293B] text-white font-bold py-3.5 px-8 rounded-lg text-[15px] transition-colors flex items-center gap-2 shadow-md"
              >
                View Internship Tracks
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path></svg>
              </button>
            </div>
          </div>
        </section>

        {/* COMPANIES WHERE LEARNERS HAVE PROGRESSED MARQUEE */}
        <section className="py-[60px] md:py-[70px] overflow-hidden bg-white">
          <div className="text-center mt-[20px] px-5">
            <span className="block text-[10px] md:text-xs font-bold text-[#0284C7] tracking-[1.5px] uppercase mb-4">SUCCESS STORIES</span>
            <h2 className="text-[32px] md:text-[46px] font-bold tracking-tight mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              <span className="text-[#0F172A]">Companies Where Our </span>
              <span className="inline-block bg-gradient-to-r from-blue-500 to-purple-500 text-transparent bg-clip-text">Learners Have Progressed</span>
            </h2>
            <p className="text-slate-500 text-base md:text-[17px] mt-4 mx-auto max-w-[700px] font-medium leading-relaxed">
              Our programs focus on building real skills that align with industry requirements <br className="hidden md:block" />
              across leading technology companies.
            </p>
          </div>
          
          <div className="relative w-[150vw] left-1/2 -translate-x-1/2 mt-8 md:mt-12 py-6 md:py-8 overflow-hidden group">
            <div className="absolute left-0 top-0 bottom-0 w-[35vw] bg-gradient-to-r from-white to-transparent z-10 pointer-events-none"></div>
            <div className="absolute right-0 top-0 bottom-0 w-[35vw] bg-gradient-to-l from-white to-transparent z-10 pointer-events-none"></div>
            
            <div className="w-full flex flex-col gap-6">
               {/* Top Line - Scrolling Left */}
              <div className="w-full overflow-hidden whitespace-nowrap">
                <div className="inline-flex gap-5 animate-scrollLeft w-max group-hover:[animation-play-state:paused]" style={{ animationDuration: '60s' }}>
                  {[...companyData, ...companyData, ...companyData].map((c, i) => (
                    <span 
                      key={i} 
                      className={`inline-flex items-center gap-2 py-3 px-6 md:px-8 bg-white border border-slate-100 rounded-2xl font-bold text-[15px] md:text-[17px] shadow-[0_4px_20px_rgba(0,0,0,0.04)] transition-all duration-300 hover:-translate-y-2 hover:scale-105 cursor-pointer ${c.color} ${c.hoverGlow}`}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="4" y="6" width="16" height="14" rx="2" ry="2"></rect>
                        <path d="M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"></path>
                      </svg>
                      {c.name}
                    </span>
                  ))}
                </div>
              </div>
               {/* Bottom Line - Scrolling Right */}
              <div className="w-full overflow-hidden whitespace-nowrap">
                <div className="inline-flex gap-5 animate-scrollRight w-max group-hover:[animation-play-state:paused]" style={{ marginLeft: '-150px', animationDuration: '60s' }}>
                  {[...companyDataBottom, ...companyDataBottom, ...companyDataBottom].map((c, i) => (
                    <span 
                      key={i} 
                      className={`inline-flex items-center gap-2 py-3 px-6 md:px-8 bg-white border border-slate-100 rounded-2xl font-bold text-[15px] md:text-[17px] shadow-[0_4px_20px_rgba(0,0,0,0.04)] transition-all duration-300 hover:-translate-y-2 hover:scale-105 cursor-pointer ${c.color} ${c.hoverGlow}`}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="4" y="6" width="16" height="14" rx="2" ry="2"></rect>
                        <path d="M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"></path>
                      </svg>
                      {c.name}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* QUOTE & BUTTON for Companies Section */}
          <div className="mt-6 flex flex-col items-center">
            <p className="text-[#94A3B8] italic text-[12px] md:text-[13px] mb-8 font-medium">
              "Logos represent companies in the broader tech ecosystem. Individual outcomes may vary."
            </p>
            <button 
              onClick={() => navigate('/apply')} 
              className="bg-[#0284C7] hover:bg-[#0369A1] text-white font-bold py-3.5 px-8 rounded-lg text-[15px] transition-colors flex items-center gap-2 shadow-md shadow-sky-200"
            >
              Explore Internship Tracks
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path></svg>
            </button>
          </div>
        </section>

        {/* CERTIFICATE SECTION WITH STATUS VERIFIED BADGE (UPDATED TO MATCH DARK THEME) */}
        <section className="bg-[#0B1120] text-white py-[80px] md:py-[110px] relative overflow-hidden">
          {/* Ambient Glows */}
          <div className="absolute top-1/2 left-0 -translate-y-1/2 w-[600px] h-[600px] bg-blue-600/10 blur-[120px] rounded-full pointer-events-none"></div>
          <div className="absolute top-1/2 right-0 -translate-y-1/2 w-[600px] h-[600px] bg-purple-600/10 blur-[120px] rounded-full pointer-events-none"></div>

          <div className="max-w-[1200px] mx-auto px-5 grid grid-cols-1 lg:grid-cols-2 gap-[40px] md:gap-[60px] items-center text-center lg:text-left relative z-10">
            <div>
              <div className="inline-flex items-center gap-2 py-1.5 px-4 border border-slate-700/60 rounded-full text-[10px] md:text-[11px] font-bold mb-6 bg-slate-900/40 text-slate-200 uppercase tracking-wider shadow-sm">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#EAB308" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z"></path>
                  <path d="M9.09 9l-2.5-3"></path>
                  <path d="M14.91 9l2.5-3"></path>
                  <path d="M9.09 15l-2.5 3"></path>
                  <path d="M14.91 15l2.5 3"></path>
                </svg> 
                VERIFIED CREDENTIAL
              </div>
              
              <h2 className="text-[32px] sm:text-[38px] lg:text-[46px] font-bold leading-tight mb-6 tracking-tight max-w-[600px] mx-auto lg:mx-0" style={{ fontFamily: "'Playfair Display', serif" }}>
                Earn a Recognized<br/>
                <span className="inline-block bg-gradient-to-r from-[#60A5FA] to-[#A78BFA] text-transparent bg-clip-text pb-1">Certificate of Completion</span>
              </h2>
              
              <p className="text-slate-300 text-base md:text-[17px] leading-relaxed mb-8 max-w-[90%] mx-auto lg:mx-0">
                Showcase your achievement. Our certificates come with a unique verification ID that can be verified instantly on our platform. Add it to your LinkedIn profile and resume to stand out to recruiters.
              </p>
              
              <div className="flex flex-col sm:flex-row justify-center lg:justify-start gap-[15px] md:gap-[20px]">
                <button onClick={() => navigate('/apply')} className="bg-[#F8FAFC] text-[#0F172A] rounded-xl py-3.5 px-6 font-bold flex items-center justify-center gap-2 hover:bg-white transition-colors">
                  Start Your Journey &rarr;
                </button>
                <button onClick={() => navigate('/verify')} className="bg-transparent border border-slate-600 text-white rounded-xl py-3.5 px-6 font-bold flex items-center justify-center gap-2 hover:bg-slate-800 transition-colors">
                  Verify a Certificate 
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
                </button>
              </div>
            </div>
            
            <div className="group px-4 sm:px-0 relative mt-8 lg:mt-0">
              <img src={certificateImg} alt="Certificate" className="w-full rounded-[24px] border border-white/10 shadow-[0_20px_50px_rgba(0,0,0,0.5)] transition-transform duration-500 group-hover:scale-[1.02] lg:group-hover:-translate-y-3 animate-low-bounce relative z-10 bg-white" />
              
              {/* Outer Glow for Image */}
              <div className="absolute inset-0 bg-white/5 blur-2xl rounded-[24px] transform group-hover:scale-105 transition-transform duration-500 pointer-events-none"></div>

              {/* Animated Status Verified Badge */}
              <div className="absolute -bottom-4 -right-2 md:-bottom-6 md:-right-6 bg-white py-3 md:py-4 px-5 md:px-6 rounded-2xl shadow-2xl flex items-center gap-3 md:gap-4 z-20 border border-slate-100 animate-[bounce_1s_infinite]">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center text-green-500">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                </div>
                <div className="flex flex-col text-left leading-none">
                  <span className="text-[10px] md:text-[11px] text-slate-400 uppercase tracking-widest font-bold mb-1">Status</span>
                  <span className="text-sm md:text-base text-slate-900 font-black tracking-wide">Verified & Valid</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* EXPLORE INTERNSHIPS */}
        <section className="py-[60px] md:py-[90px] bg-white text-center">
          <div className="max-w-[1200px] mx-auto px-5 mb-12 md:mb-16">
            <h2 className="text-[36px] md:text-[48px] font-bold mb-4 tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              Explore Our <span className="text-[#0284C7]">Internships</span>
            </h2>
            <p className="text-slate-500 text-base md:text-[16px] mx-auto max-w-[800px] mb-12 font-medium leading-relaxed">
              Choose from a variety of domains to build your skills. All internships are 4 weeks long and<br className="hidden md:block"/> project-based. Each internship includes predefined projects designed to simulate real<br className="hidden md:block"/> development tasks.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[25px] text-left items-stretch">
              {internshipsData.map((item, i) => (
                <div 
                  key={i} 
                  className="bg-white rounded-[24px] p-[30px] md:p-[35px] border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.04)] relative overflow-hidden flex flex-col h-full group transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_8px_30px_rgba(0,0,0,0.08)] cursor-pointer" 
                  onClick={() => navigate('/apply')}
                >
                  <div className={`absolute top-0 right-0 w-32 h-32 ${item.blobColor} rounded-bl-[100px] -z-10 transition-transform duration-500 group-hover:scale-110`}></div>
                  <div className={`w-[50px] h-[50px] rounded-xl flex items-center justify-center text-white mb-[25px] shadow-md ${item.bgCode}`}>
                    {item.icon}
                  </div>
                  <h3 className="text-[20px] md:text-[22px] font-bold mb-[12px] text-[#0F172A]" style={{ fontFamily: "'Playfair Display', serif" }}>{item.title}</h3>
                  <p className="text-[#475569] text-[14px] md:text-[15px] leading-relaxed flex-grow mb-[25px]">{item.desc}</p>
                  <button 
                    onClick={(e) => { e.stopPropagation(); navigate('/apply'); }} 
                    className="text-[#0284C7] font-bold text-[14px] md:text-[15px] flex items-center gap-2 hover:text-[#0369A1] transition-colors mt-auto w-fit"
                  >
                    Apply Now <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path></svg>
                  </button>
                </div>
              ))}
            </div>
            
            <div className="mt-12 md:mt-16 text-center flex flex-col items-center">
              <p className="text-slate-600 font-semibold text-[14px] md:text-[15px] mb-1">Complete selected assignments to qualify for certification.</p>
              <p className="text-slate-400 text-[12px] md:text-[13px] mb-6">Assignments are reviewed manually to ensure real skill validation.</p>
              <button 
                onClick={() => navigate('/apply')} 
                className="text-[#0284C7] font-bold text-[15px] md:text-[16px] hover:text-[#0369A1] transition-colors flex items-center gap-1 border-b-[1.5px] border-[#0284C7] pb-0.5"
              >
                View All Domains <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path></svg>
              </button>
            </div>
          </div>
        </section>

        {/* HOW IT WORKS BLOCK (REDESIGNED EXACTLY LIKE SCREENSHOT) */}
        <section className="py-[60px] md:py-[100px] text-center relative bg-white overflow-hidden">
          {/* Dotted Background */}
          <div className="absolute inset-0 bg-[radial-gradient(#CBD5E1_1px,transparent_1px)] [background-size:24px_24px] opacity-50 z-0"></div>

          <div className="max-w-[1200px] mx-auto px-5 relative z-10">
            <span className="block text-[10px] md:text-xs font-bold text-[#0284C7] tracking-[1.5px] uppercase mb-3">PROCESS</span>
            <h2 className="text-[36px] md:text-[48px] font-bold mb-12 md:mb-20" style={{ fontFamily: "'Playfair Display', serif" }}>
                <span className="text-[#0F172A]">How It </span><span className="text-[#0284C7]">Works?</span>
            </h2>

            <div className="flex flex-col md:flex-row justify-between relative text-center">
              {/* Desktop connecting solid line */}
              <div className="hidden md:block absolute top-[45px] left-[12%] right-[12%] h-[2px] z-0 bg-[#bae6fd]"></div>

              {howItWorks.map((item, i) => (
                <div key={i} className="relative z-10 flex-1 px-4 mb-12 md:mb-0 group cursor-pointer">
                  <div className="w-[90px] h-[90px] mx-auto bg-white rounded-[24px] flex items-center justify-center text-3xl mb-6 relative transition-all duration-300 shadow-[0_4px_20px_rgba(0,0,0,0.06)] border border-slate-100 group-hover:-translate-y-2 group-hover:border-sky-300 group-hover:shadow-[0_8px_30px_rgba(2,132,199,0.15)] group-hover:scale-105">
                    {item.icon}
                    <div className="absolute -top-3 -right-3 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-[12px] font-bold border-4 border-white shadow-sm transition-colors group-hover:border-sky-100">
                      {item.step}
                    </div>
                  </div>
                  <h3 className="text-[20px] md:text-[22px] font-bold mb-3 transition-colors text-slate-900 group-hover:text-[#0284C7]" style={{ fontFamily: "'Playfair Display', serif" }}>{item.title}</h3>
                  <p className="text-slate-500 text-[13px] md:text-[14px] leading-relaxed max-w-[240px] mx-auto">{item.desc}</p>
                </div>
              ))}
            </div>

            <div className="mt-16 text-center">
              <p className="text-slate-500 font-medium text-[14px] md:text-[15px]">
                Our internships focus on proving skills through completed work, not passive learning.
              </p>
            </div>
          </div>
        </section>

        {/* CAREER BOOSTERS / FREE AI CAREER TOOLS (REDESIGNED) */}
        <section className="py-[60px] md:py-[90px] bg-white text-center">
          <div className="max-w-[1200px] mx-auto px-5 mb-16 md:mb-24">
            <span className="block text-[10px] md:text-xs font-bold text-[#0284C7] tracking-[1.5px] uppercase mb-3">CAREER BOOSTERS</span>
            <h2 className="text-[36px] md:text-[48px] font-bold mb-4 tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              <span className="text-[#0F172A]">Free </span>
              <span className="text-[#0284C7]">AI </span>
              <span className="inline-block bg-gradient-to-r from-blue-500 to-purple-500 text-transparent bg-clip-text">Career Tools</span>
            </h2>
            <p className="text-slate-600 text-base md:text-[17px] mx-auto max-w-[750px] mb-3 font-medium leading-relaxed">
              Stop getting rejected by robots. Use our AI-powered toolkit to optimize your resume, beat <br className="hidden md:block"/> the ATS, and connect with recruiters instantly.
            </p>
            <p className="text-slate-500 text-[14px] md:text-[15px] mx-auto max-w-[700px] mb-12 md:mb-16">
              These tools are designed to help you prepare for internships, job applications, and recruiter screening processes.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[25px] text-left items-stretch justify-center">
              {/* First 3 tools taking 3 columns */}
              {tools.slice(0, 3).map((item, i) => (
                <div 
                  key={i} 
                  className="bg-white rounded-[24px] p-[30px] md:p-[35px] border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.04)] relative overflow-hidden flex flex-col h-full group transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_8px_30px_rgba(0,0,0,0.08)] cursor-pointer" 
                  onClick={() => navigate(item.route)}
                >
                  <div className={`absolute top-0 right-0 w-32 h-32 ${item.blobColor} rounded-bl-[100px] -z-10 transition-transform duration-500 group-hover:scale-110`}></div>
                  <div className={`w-[50px] h-[50px] rounded-xl flex items-center justify-center text-white mb-[25px] shadow-md ${item.bgCode}`}>
                    {item.icon}
                  </div>
                  <h3 className="text-[20px] md:text-[22px] font-bold mb-[12px] text-[#0F172A]" style={{ fontFamily: "'Playfair Display', serif" }}>{item.title}</h3>
                  <p className="text-[#475569] text-[14px] md:text-[15px] leading-relaxed flex-grow mb-[25px]">{item.desc}</p>
                  <button 
                    onClick={(e) => { e.stopPropagation(); navigate(item.route); }} 
                    className="text-[#0284C7] font-bold text-[14px] md:text-[15px] flex items-center gap-2 hover:text-[#0369A1] transition-colors mt-auto w-fit"
                  >
                    {item.linkText} <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path></svg>
                  </button>
                </div>
              ))}
              
              {/* 4th tool taking 1 column in the next row, aligned left */}
              <div 
                  className="bg-white rounded-[24px] p-[30px] md:p-[35px] border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.04)] relative overflow-hidden flex flex-col h-full group transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_8px_30px_rgba(0,0,0,0.08)] cursor-pointer" 
                  onClick={() => navigate(tools[3].route)}
                >
                  <div className={`absolute top-0 right-0 w-32 h-32 ${tools[3].blobColor} rounded-bl-[100px] -z-10 transition-transform duration-500 group-hover:scale-110`}></div>
                  <div className={`w-[50px] h-[50px] rounded-xl flex items-center justify-center text-white mb-[25px] shadow-md ${tools[3].bgCode}`}>
                    {tools[3].icon}
                  </div>
                  <h3 className="text-[20px] md:text-[22px] font-bold mb-[12px] text-[#0F172A]" style={{ fontFamily: "'Playfair Display', serif" }}>{tools[3].title}</h3>
                  <p className="text-[#475569] text-[14px] md:text-[15px] leading-relaxed flex-grow mb-[25px]">{tools[3].desc}</p>
                  <button 
                    onClick={(e) => { e.stopPropagation(); navigate(tools[3].route); }} 
                    className="text-[#0284C7] font-bold text-[14px] md:text-[15px] flex items-center gap-2 hover:text-[#0369A1] transition-colors mt-auto w-fit"
                  >
                    {tools[3].linkText} <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path></svg>
                  </button>
                </div>
            </div>

            <div className="mt-12 md:mt-16 text-center flex flex-col items-center">
              <p className="text-slate-500 italic text-[13px] md:text-[14px] font-medium">
                "These tools are built to support your internship journey and career preparation."
              </p>
            </div>

          </div>
        </section>

        {/* STUDENT REVIEWS SECTION */}
        <section className="py-[60px] md:py-[90px] text-center overflow-hidden bg-white">
          <div className="max-w-[1200px] mx-auto px-5 mb-8 md:mb-12">
            <h2 className="text-[36px] md:text-[48px] font-bold tracking-tight mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              <span className="text-[#0F172A]">Student </span><span className="text-[#0284C7]">Reviews</span>
            </h2>
            <p className="text-slate-500 text-base md:text-[17px] mx-auto max-w-[800px] mb-12 font-medium leading-relaxed">
              Feedback from learners who completed real assignments and earned verified certificates.
            </p>
          </div>
          <div className="w-full overflow-hidden whitespace-nowrap group mb-12">
            <div className="inline-flex gap-4 md:gap-6 animate-scrollLeft px-5 group-hover:[animation-play-state:paused]" style={{ animationDuration: '40s' }}>
              {[...reviews, ...reviews, ...reviews, ...reviews].map((rev, i) => (
                <div key={i} className="min-w-[280px] md:min-w-[350px] max-w-[280px] md:max-w-[350px] whitespace-normal bg-white border border-slate-200 p-[25px] md:p-[35px] rounded-[20px] text-left shadow-[0_4px_20px_rgba(0,0,0,0.03)] transition-transform hover:-translate-y-1">
                  <div className="flex gap-1 text-yellow-400 mb-[15px] md:mb-[18px]">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <svg key={star} width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon></svg>
                    ))}
                  </div>
                  <p className="text-slate-600 text-[14px] md:text-[15px] mb-[20px] md:mb-[25px] min-h-[60px] md:min-h-[70px] leading-relaxed">"{rev.quote}"</p>
                  <div className="flex items-center gap-[15px] md:gap-[18px] border-t border-slate-100 pt-[15px] md:pt-[18px]">
                    <div className="w-[40px] h-[40px] md:w-[45px] md:h-[45px] rounded-full bg-sky-100 text-[#0284C7] flex justify-center items-center font-bold text-[16px] md:text-[18px]">{rev.initial}</div>
                    <div>
                      <h4 className="text-[14px] md:text-[16px] font-bold m-0 text-slate-900">{rev.name}</h4>
                      <span className="text-[12px] md:text-[13px] text-slate-400">{rev.role}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-8 text-center">
            <p className="text-slate-400 italic text-[13px] md:text-[14px] font-medium">
              "Reviews reflect real experiences from learners who completed internship tasks."
            </p>
          </div>
        </section>

        {/* FAQ SECTION */}
        <section className="py-[60px] md:py-[90px] bg-white">
          <div className="max-w-[800px] mx-auto px-5">
            <h2 className="text-[36px] md:text-[48px] font-bold text-center mb-4 text-[#0F172A]" style={{ fontFamily: "'Playfair Display', serif" }}>Frequently Asked Questions</h2>
            <p className="text-slate-500 text-center mb-10 md:mb-12 text-[15px] md:text-[16px]">Got questions? We've got answers.</p>
            <div className="flex flex-col gap-[15px] md:gap-[20px]">
              {faqs.map((faq, i) => (
                <div key={i} className="bg-white border border-slate-200 rounded-[16px] overflow-hidden shadow-sm">
                  <button className="w-full p-5 md:p-6 bg-transparent border-none text-left text-[15px] md:text-[16px] font-bold flex justify-between items-center cursor-pointer text-slate-800 hover:bg-slate-50 transition-colors" onClick={() => toggleFaq(i)}>
                    {faq.question} 
                    <span className="text-slate-400 transition-transform">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`transform transition-transform duration-300 ${activeFaq === i ? 'rotate-180' : ''}`}>
                        <polyline points="6 9 12 15 18 9"></polyline>
                      </svg>
                    </span>
                  </button>
                  <div className={`px-5 md:px-6 text-slate-500 text-[14px] md:text-[15px] leading-relaxed transition-all duration-300 ease-in-out ${activeFaq === i ? 'max-h-[250px] pb-5 md:pb-6 opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}>
                    <p>{faq.answer}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* JOIN NOW / CTA SECTION */}
        <section className="py-[60px] md:py-[100px] bg-white">
          <div className="max-w-[1000px] mx-auto px-5">
            <div className="bg-[#0F172A] rounded-[32px] p-10 md:p-16 text-center relative overflow-hidden shadow-2xl">
              
              <div className="relative z-10">
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Ready to start your career?
                </h2>
                <p className="text-slate-300 text-[14px] md:text-[16px] max-w-[600px] mx-auto mb-10 leading-relaxed font-medium">
                  Join thousands of students who are already building their future with SENVRA AI. It's free, flexible, and career-focused.
                </p>
                <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
                  <button onClick={() => navigate('/apply')} className="w-full sm:w-auto py-3.5 px-8 rounded-xl bg-white text-[#0F172A] font-bold text-[15px] hover:bg-slate-100 transition-colors shadow-lg">
                    Apply Now
                  </button>
                  <button onClick={() => navigate('/contact')} className="w-full sm:w-auto py-3.5 px-8 rounded-xl bg-transparent text-white font-bold text-[15px] border border-slate-600 hover:bg-slate-800 transition-colors">
                    Contact Support
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

      </main>
    </>
  );
};

export default Home;